import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FaCheck } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import TableSkeleton from "../../TableSkeleton";
import AccountExportOptions from "../../AccountExportOptions";
import PathologyAssignTestsModal from "./PathologyAssignTestsModal";
import DynamicSelect from "../../DynamicSelect";
import Pagination from "../../Shared/Pagination";

const API_BASE = "https://api.muktihospital.com/api/pathology-performers";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const PathologyPerformerModal = ({
  isOpen,
  onClose,
  onSave,
  editing,
  form,
  setForm,
  loading,
}) => {
  const performerTypeOptions = [
    { value: "doctor", label: "Doctor" },
    { value: "technician", label: "Technician" },
  ];

  const [doctorOptions, setDoctorOptions] = useState([]);

  useEffect(() => {
    if (!isOpen || form.type !== "doctor") return;
    let active = true;
    (async () => {
      try {
        const res = await axios.get("https://api.muktihospital.com/api/doctor", {
          headers: { "x-api-key": API_KEY },
        });
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data.data)
          ? res.data.data
          : [];
        const opts = list.map((d) => {
          let nm = d.translations?.en?.name || d.name || d.slug || "N/A";
          if (d.slug && !d.name && !d.translations?.en?.name) {
            nm = d.slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
          }
          return { value: nm, label: nm };
        });
        if (active) setDoctorOptions(opts);
      } catch (err) {
        console.error("Fetch doctors error:", err);
      }
    })();
    return () => {
      active = false;
    };
  }, [isOpen, form.type]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 print-hidden">
      <div className="bg-white rounded-lg shadow-lg p-6 w-96">
        <h2 className="text-xl font-bold mb-4 text-gray-700">
          {editing ? "Edit Performer" : "Add Performer"}
        </h2>
        {form.type === "doctor" && (
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Pick from Doctor list (optional)
            </label>
            <DynamicSelect
              options={doctorOptions}
              value={doctorOptions.find((o) => o.value === form.name) || null}
              onChange={(o) => setForm({ ...form, name: o ? o.value : form.name })}
              isSearchable={true}
              isClearable={true}
              placeholder="Search doctor name..."
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Or type a new name below.
            </p>
          </div>
        )}
        <input
          type="text"
          className="w-full mb-3 px-3 py-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color"
          placeholder="Performer Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <input
          type="text"
          className="w-full mb-3 px-3 py-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color"
          placeholder="Phone (optional)"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <div className="mb-4">
          <DynamicSelect
            options={performerTypeOptions}
            value={
              performerTypeOptions.find((o) => o.value === form.type) ||
              performerTypeOptions[0]
            }
            onChange={(o) => setForm({ ...form, type: o ? o.value : "doctor" })}
            isSearchable={false}
            placeholder="Select Type..."
          />
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function PathologyPerformerList() {
  const [list, setList] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", type: "doctor" });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [assignPerformer, setAssignPerformer] = useState(null);
  const itemsPerPage = 10;

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(API_BASE, {
        headers: { "x-api-key": API_KEY },
      });
      const performers = Array.isArray(data.data) ? data.data : [];
      setList(performers);
      setTotalRecords(performers.length);
    } catch (err) {
      console.error("Fetch error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      toast.error(err.response?.data?.error || "Failed to load performers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const filteredList = list.filter(
    (item) =>
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.phone?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const currentData = filteredList.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentData.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (performerId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(performerId)
        ? prevSelectedRows.filter((id) => id !== performerId)
        : [...prevSelectedRows, performerId]
    );
  };

  const openModal = (performer = null) => {
    setEditing(performer);
    setForm({
      name: performer?.name || "",
      phone: performer?.phone || "",
      type: performer?.type || "doctor",
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.warn("Performer name is required");
      return;
    }
    const isDuplicate = list.some(
      (item) =>
        item.name.toLowerCase() === form.name.trim().toLowerCase() &&
        item.id !== editing?.id
    );
    if (isDuplicate) {
      toast.warn("Performer already exists!");
      return;
    }
    setLoading(true);
    try {
      if (editing) {
        await axios.put(`${API_BASE}/${editing.id}`, form, {
          headers: { "x-api-key": API_KEY },
        });
        toast.success("Performer updated successfully");
      } else {
        await axios.post(API_BASE, form, {
          headers: { "x-api-key": API_KEY },
        });
        toast.success("Performer added successfully");
      }
      setModalOpen(false);
      setForm({ name: "", phone: "", type: "doctor" });
      fetchList();
    } catch (err) {
      console.error("Save error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      toast.error(err.response?.data?.error || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (performerIds) => {
    try {
      if (Array.isArray(performerIds)) {
        await Promise.all(
          performerIds.map((id) =>
            axios.delete(`${API_BASE}/${id}`, {
              headers: { "x-api-key": API_KEY },
            })
          )
        );
        toast.success(`${performerIds.length} performer(s) deleted successfully`);
      } else {
        await axios.delete(`${API_BASE}/${performerIds}`, {
          headers: { "x-api-key": API_KEY },
        });
        toast.success("Performer deleted successfully");
      }
      setSelectedRows([]);
      fetchList();
    } catch (err) {
      console.error("Delete error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const headers = ["No.", "Name", "Phone", "Type"];
  const dataMapper = (item, index, array) => {
    const serial = array.indexOf(item) + 1;
    return [serial, item.name || "", item.phone || "", item.type || ""].join("\t");
  };

  const skeletonColumns = [
    { width: 20, height: 16, className: "print-hidden-in-print" },
    { width: 30, height: 16 },
    { width: 150, height: 16 },
    { width: 100, height: 16 },
    { width: 80, height: 16 },
    { width: 80, height: 16, className: "print-hidden-in-print" },
  ];

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Pathology Performers
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by name or phone"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            <div className="flex items-center justify-between w-full gap-3">
              <button
                onClick={() => openModal()}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Performer
              </button>
              <AccountExportOptions
                data={filteredList}
                title="Pathology Performer Master Database"
                subTitle={`Total Performers: ${filteredList.length}`}
                headers={headers}
                dataMapper={(item, index) => dataMapper(item, index, filteredList)}
                dropdownOpen={dropdownOpen}
                toggleDropdown={() => setDropdownOpen(!dropdownOpen)}
              />
            </div>
          </div>
        </div>
        <div>
          <style>
            {`
              @media print {
                .print-hidden, .print-hidden-in-print {
                  display: none !important;
                }
                .print-all-rows {
                  display: table-row !important;
                }
                .print-table {
                  width: 100%;
                  border-collapse: collapse;
                }
                .print-table th, .print-table td {
                  border: 1px solid #000;
                  padding: 8px;
                  text-align: left;
                  font-size: 12px;
                }
                .print-table th {
                  background-color: #f0f0f0;
                  font-weight: bold;
                }
                .print-table tbody tr:nth-child(even) {
                  background-color: #f9f9f9;
                }
              }
            `}
          </style>
          <div className="overflow-x-auto print-wrapper">
            <table className="min-w-[1000px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left print-hidden-in-print">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="agreement"
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={
                          currentData.length > 0 &&
                          currentData.every((item) =>
                            selectedRows.includes(item.id)
                          )
                        }
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="agreement"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                      ></label>
                    </div>
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    No.
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Name
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Phone
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Type
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton
                    rowCount={itemsPerPage}
                    columns={skeletonColumns}
                    baseColor="#e5e7eb"
                    highlightColor="#f3f4f6"
                  />
                ) : currentData.length === 0 ? (
                  <tr className="print-hidden">
                    <td
                      colSpan={6}
                      className="text-center py-4 capitalize font-inter font-normal text-sm text-M-heading-color"
                    >
                      No performers found.
                    </td>
                  </tr>
                ) : (
                  <>
                    {currentData.map((item, i) => {
                      const serialNumber =
                        (currentPage - 1) * itemsPerPage + i + 1;
                      return (
                        <tr
                          key={`normal-${item.id}`}
                          className="border-b hover:bg-gray-50 print-hidden"
                        >
                          <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                            <div className="relative left-3 -top-[1px]">
                              <input
                                type="checkbox"
                                id={`performer-${i}`}
                                className="hidden peer print-hidden"
                                checked={selectedRows.includes(item.id)}
                                onChange={() => handleRowSelect(item.id)}
                              />
                              <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                                <FaCheck className="text-slate-100" />
                              </span>
                              <label
                                htmlFor={`performer-${i}`}
                                className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                              ></label>
                            </div>
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {serialNumber}
                          </td>
                          <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            <span className="capitalize font-medium">{item.name || "N/A"}</span>
                            {Array.isArray(item.assignedTests) && item.assignedTests.length > 0 ? (
                              <div className="flex flex-wrap gap-1 mt-1.5 max-w-md">
                                {item.assignedTests.map((t) => (
                                  <span
                                    key={t.id}
                                    className="inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] px-1.5 py-0.5 rounded"
                                  >
                                    {t.testName}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div className="text-[10px] text-gray-300 mt-1">No tests assigned</div>
                            )}
                          </td>
                          <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {item.phone || "N/A"}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            <span
                              className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${
                                item.type === "technician"
                                  ? "bg-amber-50 text-amber-600 border border-amber-200"
                                  : "bg-blue-50 text-blue-600 border border-blue-200"
                              }`}
                            >
                              {item.type === "technician" ? "Technician" : "Doctor"}
                            </span>
                          </td>
                          <td className="p-3 text-left print-hidden-in-print align-middle">
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                onClick={() => setAssignPerformer(item)}
                                className="px-2 h-7 rounded-full bg-emerald-50 text-emerald-600 text-xs font-semibold inline-flex items-center justify-center hover:bg-emerald-600 hover:text-white transition-all duration-300 whitespace-nowrap"
                                title="Assign Tests"
                              >
                                Assign Tests
                              </button>
                              <button
                                onClick={() => openModal(item)}
                                className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                                title="Edit"
                              >
                                <FiEdit />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedRows([item.id]);
                                  setDeleteModalOpen(true);
                                }}
                                className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                                title="Delete"
                              >
                                <RiDeleteBin6Line />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {Array.isArray(filteredList) && filteredList.length > 0 ? (
                      filteredList.map((item, i) => {
                        const serialNumber = i + 1;
                        return (
                          <tr
                            key={`print-${item.id}`}
                            className="border-b print-all-rows"
                            style={{ display: "none" }}
                          >
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3 print-hidden-in-print"></td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {serialNumber}
                            </td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {item.name || "N/A"}
                            </td>
                            <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {item.phone || "N/A"}
                            </td>
                            <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                              {item.type === "technician" ? "Technician" : "Doctor"}
                            </td>
                            <td className="p-3 text-left space-x-2 print-hidden-in-print"></td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr
                        className="print-all-rows"
                        style={{ display: "none" }}
                      >
                        <td
                          colSpan={5}
                          className="text-center py-4 capitalize font-inter font-normal text-sm text-M-heading-color"
                        >
                          No performers found.
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="print-hidden">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={filteredList.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      <PathologyPerformerModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        editing={editing}
        form={form}
        setForm={setForm}
        loading={loading}
      />
      <PathologyAssignTestsModal
        performer={assignPerformer}
        onClose={() => setAssignPerformer(null)}
        onSaved={() => {
          setAssignPerformer(null);
          fetchList();
        }}
      />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="performer"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
        message={
          selectedRows.length === 1
            ? "Do you really want to delete this performer?"
            : `Do you really want to delete ${selectedRows.length} performers?`
        }
      />
    </div>
  );
}